import { useEffect, useState } from 'react';
import { useTiers } from '../hooks/useTiers';
import { getAdminSettings } from '../services/adminApi';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmationModal from '../components/ConfirmationModal';
import DeleteTierModal from '../components/DeleteTierModal';
import TierFormModal from '../components/TierFormModal';

export default function TierListPage() {
  const { tiers, loading, error, fetchTiers, createTier, updateTier, updateTierStatus, deleteTier } = useTiers();
  const [sortField, setSortField] = useState('rank');
  const [sortOrder, setSortOrder] = useState('ASC');
  
  // Admin settings for special tier identification
  const [specialTierIds, setSpecialTierIds] = useState({ freeTierId: null, waitingListTierId: null });
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    tier: null,
    action: null,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [tierModal, setTierModal] = useState({ isOpen: false, tier: null, isSubmitting: false, modalKey: 'create' });

  useEffect(() => {
    fetchTiers({ order_by: sortField, order: sortOrder });
  }, [fetchTiers, sortField, sortOrder]);

  // Fetch admin settings to identify special tiers (waiting list and free tier)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getAdminSettings();
        // Settings response has structure: { site: { free_tier, waiting_list_tier, ... } }
        const siteSettings = settings.site || {};
        setSpecialTierIds({
          freeTierId: siteSettings.free_tier || null,
          waitingListTierId: siteSettings.waiting_list_tier || null,
        });
      } catch (err) {
        console.error('Failed to fetch admin settings:', err);
        // Don't block UI if settings fail - just won't have special tier protection
      }
    };
    fetchSettings();
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // New field, default to ASC
      setSortField(field);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortOrder === 'ASC') {
      return (
        <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const formatPrice = (cents) => {
    if (cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleStatusToggle = (tier) => {
    const newStatus = tier.status === 'active' ? 'inactive' : 'active';
    setConfirmModal({
      isOpen: true,
      tier,
      action: 'status',
      newStatus,
    });
  };

  const handleConfirmStatusToggle = async () => {
    /* c8 ignore start */
    if (!confirmModal.tier) return;
    /* c8 ignore stop */

    setIsProcessing(true);
    try {
      await updateTierStatus(confirmModal.tier.id, confirmModal.newStatus);
      setConfirmModal({ isOpen: false, tier: null, action: null });
    } catch (err) {
      // Error is already set in useTiers hook
      console.error('Failed to update tier status:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseModal = () => {
    /* c8 ignore start */
    if (!isProcessing) {
    /* c8 ignore stop */
      setConfirmModal({ isOpen: false, tier: null, action: null });
      setDeleteError(null);
    }
  };

  const handleDelete = (tier) => {
    setDeleteError(null);
    setConfirmModal({
      isOpen: true,
      tier,
      action: 'delete',
    });
  };

  const openTierModal = (tier = null) => {
    // Deep-clone the tier to avoid React state sharing or proxies from other libs
    const modalTier = tier ? JSON.parse(JSON.stringify({ ...tier, features: tier.features || {} })) : null;
    setTierModal({ isOpen: true, tier: modalTier, isSubmitting: false, modalKey: tier?.id || 'create' });
  };

  const openCopyTierModal = (sourceTier) => {
    // Clone the tier, strip identity fields, and clear Stripe IDs so the user fills in new ones
    const { id: _id, stripe_price_id_monthly: _m, stripe_price_id_annual: _a, is_default: _d, ...rest } = sourceTier;
    const copiedTier = JSON.parse(JSON.stringify({
      ...rest,
      features: sourceTier.features || {},
      name: `Copy of ${sourceTier.name}`,
      stripe_price_id_monthly: null,
      stripe_price_id_annual: null,
      is_default: false,
    }));
    setTierModal({ isOpen: true, tier: copiedTier, isSubmitting: false, modalKey: `copy-${Date.now()}` });
  };

  const closeTierModal = () => {
    setTierModal({ isOpen: false, tier: null, isSubmitting: false, modalKey: 'create' });
  };

  const handleSubmitTier = async (submitData) => {
    setTierModal((m) => ({ ...m, isSubmitting: true }));
    try {
      if (tierModal.tier?.id) {
        await updateTier(tierModal.tier.id, submitData);
      } else {
        await createTier(submitData);
      }
      closeTierModal();
    } catch (err) {
      console.error('Failed to save tier:', err);
      setTierModal((m) => ({ ...m, isSubmitting: false }));
    }
  };

  const handleConfirmDelete = async (tierId, migrateTo) => {
    setIsProcessing(true);
    setDeleteError(null);
    try {
      await deleteTier(tierId, migrateTo);
      setConfirmModal({ isOpen: false, tier: null, action: null });
    } catch (err) {
      // Show error in modal to allow user to fix (e.g., select migration tier)
      setDeleteError(err.message);
      console.error('Failed to delete tier:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading && tiers.length === 0) {
    return <LoadingSpinner message="Loading tiers..." />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
        <p className="font-medium">Error loading tiers</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (tiers.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-dark-700 mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-white mb-2">No tiers found</h3>
        <p className="text-gray-400 mb-6">Get started by creating your first subscription tier.</p>
        <button
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          onClick={() => openTierModal(null)}
        >
          Create Tier
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Subscription Tiers</h1>
          <p className="text-gray-400 mt-1">Manage subscription tiers and pricing</p>
        </div>
        <button
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
          onClick={() => openTierModal(null)}
        >
          Create Tier
        </button>
      </div>

      {/* Tiers table */}
      <div className="bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
        <table className="w-full" aria-label="Subscription tiers list">
          <caption className="sr-only">
            List of subscription tiers with rank, name, pricing, and status. Use sort buttons to reorder.
          </caption>
          <thead className="bg-dark-700 border-b border-dark-600">
            <tr>
              <th className="px-6 py-3 text-left" aria-sort={sortField === 'rank' ? (sortOrder === 'ASC' ? 'ascending' : 'descending') : 'none'}>
                <button
                  onClick={() => handleSort('rank')}
                  className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider hover:text-white transition-colors"
                  aria-label={`Sort by rank ${sortField === 'rank' ? (sortOrder === 'ASC' ? 'descending' : 'ascending') : 'ascending'}`}
                >
                  Rank
                  {getSortIcon('rank')}
                </button>
              </th>
              {/* c8 ignore start */}
              <th className="px-6 py-3 text-left" aria-sort={sortField === 'name' ? (sortOrder === 'ASC' ? 'ascending' : 'descending') : 'none'}>
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider hover:text-white transition-colors"
                  aria-label={`Sort by name ${sortField === 'name' ? (sortOrder === 'ASC' ? 'descending' : 'ascending') : 'ascending'}`}
                >
                  Name
                  {getSortIcon('name')}
                </button>
              </th>
              {/* c8 ignore stop */}
              <th className="px-6 py-3 text-left">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Monthly Price
                </span>
              </th>
              <th className="px-6 py-3 text-left">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Annual Price
                </span>
              </th>
              {/* c8 ignore start */}
              <th className="px-6 py-3 text-left" aria-sort={sortField === 'status' ? (sortOrder === 'ASC' ? 'ascending' : 'descending') : 'none'}>
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider hover:text-white transition-colors"
                  aria-label={`Sort by status ${sortField === 'status' ? (sortOrder === 'ASC' ? 'descending' : 'ascending') : 'ascending'}`}
                >
                  Status
                  {getSortIcon('status')}
                </button>
              </th>
              {/* c8 ignore stop */}
              <th className="px-6 py-3 text-right">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-700">
            {tiers.map((tier) => (
              <tr 
                key={tier.id} 
                className="hover:bg-dark-750 transition-colors cursor-pointer"
                /* c8 ignore start */
                onClick={() => openTierModal(tier)}
                /* c8 ignore stop */
                role="row"
                tabIndex={0}
                onKeyDown={(e) => {
                  /* c8 ignore start */
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openTierModal(tier);
                  }
                  /* c8 ignore stop */
                }}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-white">{tier.rank}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{tier.name}</span>
                    {tier.is_default && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-blue-900/30 text-blue-400 border border-blue-500/30">
                        Default
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-300">{formatPrice(tier.price_monthly_cents)}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-300">{formatPrice(tier.price_annual_cents)}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      tier.status === 'active'
                        ? 'bg-green-900/30 text-green-400 border border-green-500/30'
                        : 'bg-gray-900/30 text-gray-400 border border-gray-500/30'
                    }`}
                  >
                    {tier.status === 'active' ? '● Active' : '○ Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="text-primary-400 hover:text-primary-300 transition-colors"
                      aria-label={`Edit ${tier.name} tier`}
                      onClick={() => openTierModal(tier)}
                    >
                      Edit
                    </button>
                    <button
                      className="text-gray-400 hover:text-white transition-colors"
                      aria-label={`Copy ${tier.name} tier`}
                      onClick={() => openCopyTierModal(tier)}
                    >
                      Copy
                    </button>
                    {/* Hide status toggle and delete action for protected tiers */}
                    {!tier.is_protected && (
                      <>
                        <button
                          className="text-gray-400 hover:text-white transition-colors"
                          aria-label={tier.status === 'active' ? `Deactivate ${tier.name} tier` : `Activate ${tier.name} tier`}
                          onClick={() => handleStatusToggle(tier)}
                        >
                          {tier.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        {!tier.is_default && (
                          <button
                            className="text-red-400 hover:text-red-300 transition-colors"
                            aria-label={`Delete ${tier.name} tier`}
                            onClick={() => handleDelete(tier)}
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal (for status toggle) */}
      {confirmModal.action === 'status' && confirmModal.tier && (
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmStatusToggle}
          title={confirmModal.newStatus === 'active' ? 'Activate Tier' : 'Deactivate Tier'}
          message={
            confirmModal.newStatus === 'active'
              ? `Are you sure you want to activate "${confirmModal.tier.name}"? Users will be able to subscribe to this tier.`
              : `Are you sure you want to deactivate "${confirmModal.tier.name}"? New subscriptions will be prevented, but existing subscribers will keep their access.`
          }
          confirmText={confirmModal.newStatus === 'active' ? 'Activate' : 'Deactivate'}
          cancelText="Cancel"
          confirmVariant={confirmModal.newStatus === 'active' ? 'primary' : 'danger'}
          isProcessing={isProcessing}
        />
      )}

      {/* Delete Modal (with migration selection) */}
      {confirmModal.action === 'delete' && confirmModal.tier && (
        <DeleteTierModal
          isOpen={confirmModal.isOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmDelete}
          tier={confirmModal.tier}
          availableTiers={tiers}
          isProcessing={isProcessing}
          errorMessage={deleteError}
        />
      )}

      {/* Create/Edit Tier Modal */}
      <TierFormModal
        key={tierModal.modalKey}
        isOpen={tierModal.isOpen}
        onClose={closeTierModal}
        onSubmit={handleSubmitTier}
        tier={tierModal.tier}
        isSubmitting={tierModal.isSubmitting}
        specialTierIds={specialTierIds}
      />
    </div>
  );
}
